/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import { useQuery } from '@tanstack/react-query';
import { kycService } from '../services/api';
import { KYC, KYCStatus } from '../types';
import { isKYCEnforcementEnabled } from '../config/featureFlags';
import { useAuthStore } from '../store/authStore';

interface KYCStatusResult {
  kyc: KYC | null;
  isLoading: boolean;
  isError: boolean;
  isKYCVerified: boolean;
  kycStatus: KYCStatus | null;
  refetch: () => void;
}

/**
 * Hook to check KYC verification status
 * Returns the KYC data and helper flags for verification status
 * When KYC enforcement is disabled, always returns verified
 */
export const useKYCStatus = (): KYCStatusResult => {
  const enforcementEnabled = isKYCEnforcementEnabled();
  const { isAuthenticated, isLoading: authLoading } = useAuthStore();

  const { data, isLoading, isPending, isError, refetch } = useQuery({
    queryKey: ['kyc'],
    queryFn: async () => {
      const response = await kycService.getStatus();
      return response.data as KYC;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
    enabled: enforcementEnabled && isAuthenticated && !authLoading, // Skip API call when enforcement is disabled or not authenticated
  });

  // When KYC enforcement is disabled, treat as verified
  const isKYCVerified = !enforcementEnabled || data?.status === 'verified' || data?.didit_status === 'Approved';
  const kycStatus = enforcementEnabled ? (data?.status || null) : 'verified';

  return {
    kyc: data || null,
    isLoading: enforcementEnabled ? (isLoading || isPending) : false,
    isError,
    isKYCVerified,
    kycStatus,
    refetch,
  };
};

// Resource descriptions for KYC modal
export const resourceDescriptions = {
  project: {
    title: 'What is a Project?',
    description: 'Projects are organizational containers for managing your cloud resources and team collaboration. They help you structure workloads, enable collaborative access control, and organize billing across your team.',
    icon: 'folder',
  },
  pod: {
    title: 'What is a Pod?',
    description: 'Pods are containerized application environments, best for running Docker-based workloads, ML training, and Jupyter notebooks. They provide isolated compute resources with GPU acceleration support.',
    icon: 'cube',
  },
  vm: {
    title: 'What is a Virtual Machine?',
    description: 'Virtual Machines provide full operating system control, best for custom software stacks and dedicated GPU instances. They offer maximum flexibility with direct hardware access for high-performance computing.',
    icon: 'server',
  },
  storage: {
    title: 'What is Storage?',
    description: 'Storage provides persistent data solutions including Object Storage for scalable cloud-based file storage and NFS Volumes for shared, persistent network storage across your instances.',
    icon: 'database',
  },
  notebook: {
    title: 'What is Serverless GPU?',
    description: 'Serverless GPU provides on-demand GPU access for quick Jupyter notebook sessions without managing infrastructure. Perfect for data science, ML experimentation, and interactive computing.',
    icon: 'cpu',
  },
  template: {
    title: 'What is a Template?',
    description: 'Templates are pre-configured launch configurations for pods and VMs. They save your preferred settings including CPU, memory, GPU, and environment variables for quick deployment.',
    icon: 'document',
  },
  sshKey: {
    title: 'What is an SSH Key?',
    description: 'SSH Keys provide secure authentication for accessing your pods and VMs via SSH. They enable passwordless, encrypted connections to your cloud resources.',
    icon: 'key',
  },
} as const;

export type ResourceType = keyof typeof resourceDescriptions;
